import React from 'react';
import LegalLayout from '../components/LegalLayout';

const DracoGermanPrivacy: React.FC = () => {
  return (
    <LegalLayout title="DracoGerman Privacy Policy" lastUpdated="October 7, 2026">
      <p>
        DracoGerman is an app for learning German, made by Spiegelei Studio (see the Impressum
        in the footer below for our company details). Spiegelei Studio is the controller of the
        personal data described here. This policy explains what data the app handles, why, and
        which service providers are involved.
      </p>

      <h2>Summary</h2>
      <ul>
        <li>All learning content works without an account. No account, no personal data.</li>
        <li>
          An account is only needed for pronunciation scoring, AI analysis in Live Listening,
          and a Premium subscription.
        </li>
        <li>DracoGerman contains no advertising and no analytics or tracking SDKs.</li>
        <li>We do not sell your data and do not use it to build advertising profiles.</li>
        <li>Your recordings are processed to produce a result and are not stored by us.</li>
        <li>You can delete your account at any time inside the app.</li>
      </ul>

      <h2>Data stored only on your device</h2>
      <p>
        The following never leaves your device and is deleted when you delete the app:
      </p>
      <ul>
        <li>Your learning progress, such as which words you marked as unknown, to review, or mastered.</li>
        <li>Your settings: interface language, German level, and playback speed.</li>
        <li>The downloaded copy of the learning content and any audio saved for offline use.</li>
        <li>The live transcript in Live Listening, which is kept in memory only while the screen is open.</li>
      </ul>

      <h2>Account data</h2>
      <p>
        If you create an account, we store your email address, an optional display name, the
        sign-in method you used (email and password, Sign in with Apple, or Google), the date
        the account was created, and your subscription status. Accounts are provided by Google
        Firebase Authentication and Cloud Firestore. Passwords are handled by Firebase; we never
        see them. If you use Sign in with Apple or Google, we receive only the identifier and
        email address that provider shares with us.
      </p>
      <p>
        Your account profile (email address, display name, subscription status) is stored in a
        Cloud Firestore database located in Frankfurt, Germany. Sign-in itself is handled by
        Firebase Authentication, which processes sign-in data such as your email address on
        Google servers in the United States.
      </p>
      <p>
        We use this data to sign you in, to apply the daily usage limits of the AI features to
        your account, and to know whether you have an active Premium subscription.
      </p>

      <h2>Microphone and voice data</h2>
      <p>
        DracoGerman asks for microphone access for two features. You can use the rest of the app
        without granting it.
      </p>
      <ul>
        <li>
          <strong>Pronunciation scoring.</strong> When you submit a recording for scoring, the
          recording and the sentence you were practising are sent through our server to
          Microsoft Azure Speech, which returns the pronunciation assessment. The recording is
          used only for that request. We do not store it, and it is not linked to your profile.
          Recording and playing back your own voice without scoring stays on your device.
        </li>
        <li>
          <strong>Live Listening.</strong> The app turns German audio playing near your device
          into text using the speech recognition built into your operating system. Where your
          device supports it, this happens on the device. Otherwise the audio is processed by
          the speech recognition service of your operating system's provider (Apple or Google)
          under their privacy terms. DracoGerman does not record or store this audio.
        </li>
      </ul>

      <h2>AI analysis in Live Listening</h2>
      <p>
        When you ask for an AI explanation or a summary, the text you selected, a few
        neighbouring sentences for context, and your German level are sent through our server
        to the Qwen language model operated by Alibaba Cloud, which returns the explanation.
        For a summary, the transcript of the current session is sent. This text comes from the
        audio you were listening to. Your account details are not included in the request, and
        we do not store the text or the result on our servers.
      </p>
      <p>
        Live Listening is designed for publicly available German audio such as podcasts, news,
        and videos. Please do not use it for private conversations, meetings, or other audio
        that contains personal or confidential information about you or other people, and do
        not send such text for AI analysis.
      </p>

      <h2>Usage limits</h2>
      <p>
        To apply daily limits, our servers keep a counter of how many times your account used
        pronunciation scoring and AI analysis on the current day. The counter is stored with
        Cloudflare, contains no content, and expires automatically after two days.
      </p>

      <h2>Subscriptions</h2>
      <p>
        Premium subscriptions are purchased through the Apple App Store. Apple processes the
        payment; we never receive your payment details. To manage subscriptions we use
        RevenueCat, which receives your account identifier and purchase information (product,
        purchase and expiry dates, renewal status) from Apple. We store whether your
        subscription is active and when the current period ends.
      </p>

      <h2>Service providers</h2>
      <ul>
        <li>
          Google Firebase (Google Ireland Limited): accounts, account data, learning content.
          Database in Frankfurt, Germany; sign-in processed in the United States.
        </li>
        <li>Cloudflare, Inc.: our servers, usage counters, and delivery of audio files.</li>
        <li>
          Microsoft Azure Speech (Microsoft Ireland Operations Limited): pronunciation assessment,
          processed in the United States.
        </li>
        <li>Alibaba Cloud (Qwen): AI analysis and summaries in Live Listening, processed in China.</li>
        <li>RevenueCat, Inc.: subscription management.</li>
        <li>Apple: payments, Sign in with Apple, and speech recognition on Apple devices.</li>
      </ul>
      <p>
        Some of these providers process data outside the European Union, including in the
        United States and, for AI analysis, in China. Where required, such transfers are based
        on the safeguards provided for by data protection law, such as standard contractual
        clauses.
      </p>

      <h2>Legal bases</h2>
      <p>
        We process account, voice, and subscription data to provide the features you request
        (Art. 6(1)(b) GDPR). Usage counters and measures against abuse are based on our
        legitimate interest in keeping the service available and affordable (Art. 6(1)(f)
        GDPR). Microphone access is only used after you grant the permission on your device.
      </p>

      <h2>How long we keep data</h2>
      <ul>
        <li>Account data: until you delete your account.</li>
        <li>Recordings and text sent for scoring or AI analysis: not stored by us after the request.</li>
        <li>Usage counters: two days.</li>
        <li>
          Purchase records: as long as required for accounting and tax purposes, kept by Apple
          and RevenueCat.
        </li>
      </ul>

      <h2>Deleting your account</h2>
      <p>
        You can delete your account in the app under Account &gt; Delete account. This
        permanently deletes your sign-in and your account data. Data stored only on your device
        is removed when you delete the app. Deleting your account does not cancel a running
        subscription; cancel it in your App Store account settings.
      </p>

      <h2>Your rights</h2>
      <p>
        Under the GDPR you have the right to access, correct, or delete your data, to restrict
        or object to its processing, and to receive it in a portable format. To exercise these
        rights, write to the address below. You also have the right to lodge a complaint with
        a data protection supervisory authority.
      </p>

      <h2>Children</h2>
      <p>
        DracoGerman is intended for teenagers and adults learning German and is not directed at
        children under 13. We do not knowingly collect personal data from children under 13.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If the app changes in a way that affects your data, we will update this page and revise
        the "last updated" date above.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this policy or DracoGerman can be sent to{' '}
        <a href="mailto:service@spiegeleistudio.com">service@spiegeleistudio.com</a>.
      </p>
    </LegalLayout>
  );
};

export default DracoGermanPrivacy;
